<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>Student Progress Tracker</v-toolbar-title>
      <v-spacer></v-spacer>
      <export v-if="isAdmin || isAdmiss"></export>
      <email v-if="user.username === 'jon.moon'"></email>
      <tracker-filters :small-button="false"></tracker-filters>
    </v-toolbar>
    <v-data-table :headers="headers" :items="students" :server-items-length="studentCount" :options="pagination" @update:options="updatePagination">
      <template v-slot:item="{ item }">
        <tr @click="$router.push('/admin/tracker/' + item._id)" style="cursor:pointer">
          <td>{{ item.last }}, {{ item.preferred }}</td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ (item.done.sections / sectionCount * 100).toFixed(2) }}%</span>
              </template>
              <span>Completed {{ item.done.sections }} out of {{ sectionCount }} sections.</span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ (item.done.slides / slideCount * 100).toFixed(2) }}%</span>
              </template>
              <span>Completed {{ item.done.slides }} out of {{ slideCount }} slides.</span>
            </v-tooltip>
          </td>
          <td v-for="{ code: section } in sections" :key="'section-' + section">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" :color="getIconColor(item[section])">{{ getIcon(item[section]) }}</v-icon>
              </template>
              <span v-if="section in item && item[section].status === 'Complete'">Section has been completed.</span>
              <span v-else-if="section in item && item[section].status === 'In-Progress'">In progress; Viewed {{ item[section].highSlide || item[section].lastSlide || 0 }} out of {{ sectionHash[section] }} slide{{ sectionHash[section] > 1 ? 's' : '' }}</span>
              <span v-else-if="section in item && item[section].status === 'Ready'">Section is ready to begin but student has not started it</span>
              <span v-else>Section is currently locked so student cannot access it.</span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ dateDiff(new Date(item.updatedAt), new Date(), 'days') === '0 days' ? 'Updated Today' : 'Updated ' + dateDiff(new Date(item.updatedAt), new Date(), 'hour') + ' ago' }}</span>
              </template>
              <span>Tracker was last updated at {{ new Date(item.updatedAt).toString() }}.</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import { dateDiff } from '@/helpers/formatters'
import { computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
export default {
  components: {
    TrackerFilters: () => import('@/components/admin/tracker/filters'),
    Email: () => import('@/components/admin/email'),
    Export: () => import('@/components/admin/export')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const isAdmin = computed(() => root.$store.getters.isAdmin)
    const isAdmiss = computed(() => root.$store.getters.hasRole('Admissions'))
    const term = computed(() => root.$store.state.admin.term)
    const students = computed(() => root.$store.state.admin.students)
    const studentCount = computed(() => root.$store.state.admin.studentCount)
    const pagination = computed(() => root.$store.state.admin.pagination)
    const sections = computed(() => root.$store.state.admin.sections)
    const sectionHash = computed(() => {
      let obj = {}
      for (let i = 0; i < sections.value.length; i++) {
        obj[sections.value[i].code] = sections.value[i].slideCount
      }
      return obj
    })
    const sectionCount = computed(() => root.$store.state.admin.sectionCount)
    const slideCount = computed(() => root.$store.state.admin.slideCount)
    const headers = computed(() => {
      let arr = [
        { text: 'Name', value: 'last' },
        { text: 'Section Progress', value: 'sections' },
        { text: 'Slide % Complete', value: 'slides' }
      ]
      for (let i = 0; i < sections.value.length; i++) {
        arr.push({ text: sections.value[i].code, value: sections.value[i].code })
      }
      arr.push({ text: 'Time since last update', value: 'lastUpdateInterval' })
      return arr
    })

    const onPatchedListener = (data) => {
      for (let i = 0; i < students.value.length; i++) {
        if (students.value[i]._id === data._id) {
          root.$store.dispatch('admin/updateStudent', { index: i, data })
        }
      }
    }
    onMounted(() => { root.$feathers.service('orientation/tracker').on('patched', onPatchedListener) })
    onBeforeUnmount(() => { root.$feathers.service('orientation/tracker').removeListener('patched', onPatchedListener) })

    onMounted(() => {
      if (!root.$store.getters.hasRole('Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
        return
      }
      if (root.$store.state.admin.term !== '') {
        selectTerm()
      }
    })
    async function selectTerm () {
      root.$store.dispatch('admin/loadSections', root.$store.state.admin.term)
      root.$store.dispatch('admin/loadStudents')
    }
    watch(term, () => {
      selectTerm()
    })

    function updatePagination (options) {
      root.$store.commit('admin/setPagination', options)
      root.$store.dispatch('admin/loadStudents')
    }

    function getIconColor (item) {
      let status = 'Locked'
      if (item != null && 'status' in item) status = item.status
      switch (status) {
        case 'Locked':
          return 'error'
        case 'Complete':
          return 'success'
      }
      return ''
    }

    function getIcon (item) {
      let status = 'Locked'
      if (item != null && 'status' in item) status = item.status
      switch (status) {
        case 'Locked':
          return 'fal fa-lock'
        case 'Ready':
          return 'fal fa-play'
        case 'In-Progress':
          return 'fal fa-play-circle'
        case 'Complete':
          return 'fas fa-check'
      }
    }

    return {
      user,
      isAdmin,
      isAdmiss,
      term,
      students,
      studentCount,
      pagination,
      sections,
      sectionHash,
      sectionCount,
      slideCount,
      headers,
      selectTerm,
      updatePagination,
      getIconColor,
      getIcon,
      dateDiff
    }
  }
}
</script>
